import { Component, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { BehaviorSubject } from 'rxjs';
import { DatePipe } from '@angular/common';
import { DataSourceTypeUtil } from 'src/app/domainObjects/dataSourceType';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from 'src/app/base/base.component';
import { DataTransformerConfig } from 'src/app/domainObjects/dataTransformConfig';
import { ConfigService } from 'src/app/services/config/config.service';
import { ErrorReportingService } from 'src/app/services/errorReporting/error-reporting.service';
import { NewSingleStringValueDialogComponent } from '../shared/new-single-string-value-dialog/new-single-string-value-dialog.component';
import { v4 as uuidv4 } from 'uuid';


@Component({
    selector: 'app-config-list-view',
    templateUrl: './config-list-view.component.html',
    styleUrls: ['./config-list-view.component.css'],
    standalone: false
})
export class ConfigListViewComponent extends BaseComponent implements OnInit {

  hasError = false;
  hasConfigs = false;
  mappingConfigsSubject = new BehaviorSubject<DataTransformerConfig[]>(null);
  mappingConfigs = this.mappingConfigsSubject.asObservable();
  displayedColumns = ['name', 'dataSourceType', 'createdDateTime', 'modifiedDateTime', 'modifiedByUser', 'actions'];

  DataSourceTypeUtil = DataSourceTypeUtil;
  selectedConfigId: number;
  selectedConfigGuid: string;

  constructor(
    private configService: ConfigService,
    errorLogger: ErrorReportingService,
    matDialog: MatDialog,
    private snackBar: MatSnackBar,
    public datePipe: DatePipe,
    private route: ActivatedRoute,
    private router: Router) {
    super(errorLogger, matDialog);
  }

  ngOnInit(): void {
    this.loadConfigs();
  }

  duplicateConfig(config: DataTransformerConfig): void {
    const proposedName = this.generateProposedName(config, 1);

    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '400px';
    dialogConfig.data = {title: 'Duplicate Pipeline', stringValue: proposedName, placeholder: 'Enter Pipeline name'};

    const dialogRef = this.matDialog.open(NewSingleStringValueDialogComponent, dialogConfig);

    dialogRef.afterClosed().subscribe( async result => {
      if (!result.stringValue) { return; }

      const dialog = this.showBusyDialog('Duplicating pipeline...');

      try {
        let newConfig = new DataTransformerConfig();
        newConfig = Object.assign(newConfig, config);
        newConfig.guid = uuidv4();
        newConfig.name = result.stringValue;
        newConfig.transforms.forEach(t => t.guid = uuidv4());
        newConfig.createdDateTime = null;
        newConfig.modifiedDateTime = null;

        newConfig = await this.configService.upsertConfig(newConfig);
        //console.log('duplicated config: ' + JSON.stringify(newConfig));
        const configs = this.mappingConfigsSubject.value;
        configs.push(newConfig);
        configs.sort( (a, b) =>
          b.modifiedDateTime.getTime() - a.modifiedDateTime.getTime()
        );

        this.mappingConfigsSubject.next(configs);
      } catch (error) {
        this.displayError('Failed to duplicate pipeline', 'Error', error);
      } finally {
        dialog.close();
      }
    });
  }

  private generateProposedName(config: DataTransformerConfig, proposedCount: number): string {
    const countIndexStart = config.name.lastIndexOf('(');
    const countIndexEnd = config.name.lastIndexOf(')');
    let baseName = config.name;

    const countString = String(proposedCount);
    if (countIndexStart !== -1 && countIndexEnd !== -1) {
      baseName = config.name.substring(0, countIndexStart - 1);
    }

    const count = Number(countString) + 1;
    const proposedName = baseName + ` (${count})`;
    const configNames = this.mappingConfigsSubject.value.map(c => c.name);
    if (configNames.find(n => n === proposedName)) {
      return this.generateProposedName(config, proposedCount + 1);
    }

    return proposedName;
  }

  async loadConfigs(): Promise<void> {
    const organizationId = 1; // TODO: Get value from AppViewModel

    try {
      const configs = await (await this.configService.getAllConfigs(organizationId)).sort( (a, b) =>
        b.modifiedDateTime.getTime() - a.modifiedDateTime.getTime()
      );

      this.mappingConfigsSubject.next(configs);
      //console.log(configs);
      this.hasError = false;
      this.hasConfigs = configs.length > 0;

      //console.log('Mapping Configs loaded:  ' + JSON.stringify(configs));
    } catch (err) {
      this.hasConfigs = false;
      this.hasError = true;
      //console.log(err);
      //this.displayError('Failed to load mapping configs', 'Error', err);
    }
  }

  async loadConfigDetail(config: DataTransformerConfig): Promise<void> {
      this.selectedConfigId = config.id;
      this.selectedConfigGuid = config.guid;

      const busyDialog = this.showBusyDialog('Downloading pipeline...');
      try {
        const configJson = await this.configService.getConfigByGuidAsync(config.guid);
        //this.router.navigateByUrl(`dataTransformConfig/${config.id}/configDetail`, {state: configJson});
        this.router.navigateByUrl(`dataTransformConfig/${config.guid}/configDetail`, {state: {config: configJson}});
      } catch (err) {
        this.displayError('Failed to download pipeline', 'Error', err);
      } finally {
        busyDialog.close();
      }
  }

  async deleteConfig(config: DataTransformerConfig): Promise<void> {
    this.showConfirmDialog('Delete Confirmation', `Are you sure you want to delete "${config.name}" pipeline?`, async (confirmed) => {
      if (confirmed) {
        try {
          const result = await this.configService.deleteConfigByGuid(config.guid);
          if (result) {
            this.snackBar.open(`Successfully deleted pipeline`, 'Dismiss', {
              duration: 5000
            });

            await this.loadConfigs();
          } else {
            this.snackBar.open(`Failed to delete pipeline`, 'Dismiss', {
              duration: 5000,
              panelClass: 'snackbar-error'
            });
          }
        } catch (err) {
          this.displayError('Failed to delete pipeline', 'Error', err);
        }
      }
    });
  }
}
